import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Upload } from 'lucide-react';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';
import { BannerPreview } from './BannerCreative';
import { formatBannerDetail } from '../lib/bannerDetail';

const MAX_RAW_BYTES = 20 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/**
 * Employer edit of an ACTIVE banner — headline + image + focal point ONLY (spec §5;
 * never the linked job/duration — to feature a different job, create a new banner).
 * Free: no credit change. PATCH /api/banners/{id} (multipart). The live preview uses
 * the shared BannerCreative at the carousel's real desktop crop.
 *
 * Mount/unmount controlled by the parent — call onClose(didSave) to dismiss.
 */
const BannerEditModal = ({ banner, job, onClose }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [headline, setHeadline] = useState(banner?.headline || job?.title || '');
  const [imagePosition, setImagePosition] = useState(
    typeof banner?.image_position === 'number' ? banner.image_position : 50
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(banner?.image_url || null);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fileName = file.name?.toLowerCase() || '';
    const hasValidExtension = /\.(jpe?g|png|webp)$/i.test(fileName);
    if (!ALLOWED_TYPES.includes(file.type) && !(file.type === '' && hasValidExtension)) {
      toast.error(t('banner.errorImageType', 'Only JPEG, PNG, or WebP images are allowed.'));
      return;
    }
    if (file.size > MAX_RAW_BYTES) {
      toast.error(t('banner.errorImageSize', 'Image must be smaller than 20MB.'));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setImagePreviewUrl(reader.result);
    reader.readAsDataURL(file);
    setCompressing(true);
    try {
      const compressed = await compressImage(file, 'jobImage');
      setImageFile(compressed);
    } catch {
      setImageFile(file);
    } finally {
      setCompressing(false);
    }
  };

  const handleSave = async () => {
    if (submitting || compressing) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error(t('common.loginRequired', 'Please log in.'));
        return;
      }
      const form = new FormData();
      form.append('headline', headline.trim());
      form.append('image_position', String(imagePosition));
      if (imageFile) {
        form.append('file', imageFile, imageFile.name || 'banner.jpg');
      }
      await api.patch(`/api/banners/${banner.id}`, form, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        timeout: UPLOAD_TIMEOUT,
      });
      toast.success(t('banner.editSuccess', 'Banner updated.'));
      onClose(true);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(t('banner.errorEditNotActive', 'This banner is no longer active and cannot be edited.'));
      } else {
        toast.error(t('banner.editError', 'Failed to update the banner. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next && !submitting) onClose(false); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('banner.editTitle', 'Edit your banner')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Label htmlFor="banner-edit-headline">{t('banner.headlineLabel', 'Headline')}</Label>
          <Input
            id="banner-edit-headline"
            value={headline}
            maxLength={80}
            onChange={(e) => setHeadline(e.target.value)}
            data-testid="banner-edit-headline-input"
          />

          <p className="text-xs font-medium text-muted-foreground">
            {t('banner.livePreviewLabel', 'Live preview — exactly what publishes')}
          </p>
          <div>
            <p className="text-[10px] text-muted-foreground mb-1">{t('banner.previewDesktop', 'Desktop')}</p>
            <BannerPreview
              mode="desktop"
              imageUrl={imagePreviewUrl}
              imagePosition={imagePosition}
              headline={headline || job?.title || ''}
              detail={formatBannerDetail(job)}
              label=""
              tag="sponsored"
              cta="apply"
              showReport={false}
            />
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground mb-1">{t('banner.previewMobile', 'Mobile')}</p>
            <BannerPreview
              mode="mobile"
              className="w-3/5"
              imageUrl={imagePreviewUrl}
              imagePosition={imagePosition}
              headline={headline || job?.title || ''}
              detail={formatBannerDetail(job)}
              label=""
              tag="sponsored"
              cta="apply"
              showReport={false}
            />
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={compressing} className="w-full">
            <Upload className="mr-2 h-4 w-4" />
            {compressing ? t('banner.compressing', 'Processing image...') : t('banner.replaceImage', 'Replace image (optional)')}
          </Button>

          <div className="space-y-1">
            <Label className="text-xs">{t('banner.adjustFocus', 'Adjust focal point')}</Label>
            <input
              type="range"
              min="0"
              max="100"
              value={imagePosition}
              onChange={(e) => setImagePosition(parseInt(e.target.value, 10))}
              className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer accent-primary"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onClose(false)} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={submitting || compressing} data-testid="banner-edit-save-btn">
            {submitting ? '...' : t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BannerEditModal;
